"use client";

import { useState } from "react";
import Image from "next/image";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { queryKeys } from "@/lib/query-keys";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MarkdownRenderer } from "@/components/ui/markdown-renderer";
import FileText from "lucide-react/dist/esm/icons/file-text";
import BookOpen from "lucide-react/dist/esm/icons/book-open";
import { formatRelativeTime } from "@/lib/format";

interface SellerPost {
  id: string;
  title: string;
  slug: string;
  body_markdown: string;
  cover_image: string | null;
  tags: string[] | null;
  published_at: string | null;
}

interface SellerPostsTabProps {
  sellerId: string;
}

/** First ~160 chars of the markdown body with syntax characters stripped. */
function excerpt(markdown: string): string {
  const plain = markdown
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/[#>*_`~-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return plain.length > 160 ? `${plain.slice(0, 157)}…` : plain;
}

export function SellerPostsTab({ sellerId }: SellerPostsTabProps) {
  // Posts open in-place — there is no standalone post route, so cards must
  // never link out (the old `/blog/:slug` link 404'd).
  const [openPost, setOpenPost] = useState<SellerPost | null>(null);

  const { data: posts, isLoading } = useQuery({
    queryKey: queryKeys.sellerDashboard.posts(sellerId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("seller_posts")
        .select("id, title, slug, body_markdown, cover_image, tags, published_at")
        .eq("author_id", sellerId)
        .eq("is_published", true)
        .order("published_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) satisfies SellerPost[];
    },
    enabled: !!sellerId,
    staleTime: 2 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <section>
        <div className="flex items-center gap-2 mb-4">
          <BookOpen className="h-5 w-5 text-primary" />
          <h2 className="font-display text-lg font-bold">Posts</h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="bg-card border border-border rounded-xl">
              <Skeleton className="h-40 w-full rounded-t-xl" />
              <CardContent className="p-4 space-y-2">
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <section>
        <div className="flex items-center gap-2 mb-4">
          <BookOpen className="h-5 w-5 text-primary" />
          <h2 className="font-display text-lg font-bold">Posts</h2>
        </div>
        <div className="flex flex-col items-center justify-center py-12 text-center rounded-xl border border-border bg-muted/30">
          <FileText className="h-10 w-10 text-muted-foreground mb-3" />
          <p className="text-muted-foreground text-sm">No posts yet.</p>
        </div>
      </section>
    );
  }

  return (
    <section>
      <div className="flex items-center gap-2 mb-4">
        <BookOpen className="h-5 w-5 text-primary" />
        <h2 className="font-display text-lg font-bold">Posts</h2>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {posts.map((post) => (
          <Card
            key={post.id}
            role="button"
            tabIndex={0}
            onClick={() => setOpenPost(post)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setOpenPost(post);
              }
            }}
            aria-label={`Read post: ${post.title}`}
            className="bg-card border border-border rounded-xl overflow-hidden group hover:border-primary/40 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            {post.cover_image ? (
              <div className="relative h-40 w-full overflow-hidden">
                <Image
                  src={post.cover_image}
                  alt={post.title}
                  fill
                  sizes="(max-width: 640px) 100vw, 50vw"
                  className="object-cover"
                />
              </div>
            ) : (
              <div className="h-40 w-full gradient-primary opacity-70" />
            )}
            <CardContent className="p-4 space-y-2">
              <h3 className="font-display font-semibold text-base leading-snug group-hover:text-primary transition-colors line-clamp-2">
                {post.title}
              </h3>
              {post.body_markdown && (
                <p className="text-muted-foreground text-sm line-clamp-2">{excerpt(post.body_markdown)}</p>
              )}
              {Array.isArray(post.tags) && post.tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {post.tags.slice(0, 3).map((tag) => (
                    <Badge
                      key={tag}
                      variant="secondary"
                      className="text-2xs font-mono px-1.5 py-0"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between pt-1">
                <span className="text-label font-mono text-muted-foreground">
                  {post.published_at ? formatRelativeTime(post.published_at) : ""}
                </span>
                <span className="text-xs font-mono text-primary group-hover:underline">
                  Read more →
                </span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!openPost} onOpenChange={(open) => !open && setOpenPost(null)}>
        <DialogContent className="max-w-2xl max-h-[85dvh] overflow-y-auto">
          {openPost && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl leading-snug pr-6">
                  {openPost.title}
                </DialogTitle>
              </DialogHeader>
              {openPost.cover_image && (
                <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-muted">
                  <Image
                    src={openPost.cover_image}
                    alt={openPost.title}
                    fill
                    sizes="(max-width: 640px) 100vw, 672px"
                    className="object-cover"
                  />
                </div>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                {openPost.published_at && (
                  <span className="text-label font-mono text-muted-foreground">
                    {formatRelativeTime(openPost.published_at)}
                  </span>
                )}
                {Array.isArray(openPost.tags) &&
                  openPost.tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-2xs font-mono px-1.5 py-0">
                      {tag}
                    </Badge>
                  ))}
              </div>
              <MarkdownRenderer content={openPost.body_markdown} size="sm" />
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
